import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const PartnerDOsPage = ({ isAdmin = false }) => {
  const [dos, setDos] = useState([]);
  const [partners, setPartners] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    partner_id: '', pharmacy_id: '',
    line_items: [{ product_id: '', batch_number: '', expiry_month: '', expiry_year: '', quantity: 1 }]
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [dosRes, partnersRes, pharmsRes, prodsRes] = await Promise.all([
        api.get('/partner-dos'),
        api.get('/partners').catch(() => ({ data: [] })),
        api.get('/pharmacies'),
        api.get('/products')
      ]);
      setDos(dosRes.data);
      setPartners(partnersRes.data);
      setPharmacies(pharmsRes.data);
      setProducts(prodsRes.data);
    } catch (error) { console.error(error); }
  };

  const addLineItem = () => setForm({ ...form, line_items: [...form.line_items, { product_id: '', batch_number: '', expiry_month: '', expiry_year: '', quantity: 1 }] });
  const removeLineItem = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });
  const updateLine = (idx, field, value) => {
    const items = [...form.line_items];
    items[idx][field] = value;
    setForm({ ...form, line_items: items });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        pharmacy_id: form.pharmacy_id,
        line_items: form.line_items.map(li => ({
          product_id: li.product_id, batch_number: li.batch_number,
          expiry_month: parseInt(li.expiry_month), expiry_year: parseInt(li.expiry_year),
          quantity: parseInt(li.quantity)
        }))
      };
      if (isAdmin) payload.partner_id = form.partner_id;
      await api.post('/partner-dos', payload);
      toast.success('Partner DO created');
      setShowModal(false);
      setForm({ partner_id: '', pharmacy_id: '', line_items: [{ product_id: '', batch_number: '', expiry_month: '', expiry_year: '', quantity: 1 }] });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const partnerProducts = products.filter(p => p.owner_type === 'partner' && (!isAdmin || p.owner_id === form.partner_id));

  return (
    <div className={isAdmin ? "p-4 md:p-8 space-y-6" : "p-4 space-y-4"}>
      <div className="flex justify-between items-center">
        <h1 className={isAdmin ? "text-4xl font-bold" : "text-3xl font-bold"} style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>Partner Direct-Delivery DOs</h1>
        <Button data-testid="new-partner-do-btn" onClick={() => setShowModal(true)} style={{ backgroundColor: '#1E3A8A' }}>
          <Plus size={16} className="mr-2" /> New DO
        </Button>
      </div>

      <div className="space-y-3">
        {dos.map((doItem) => (
          <Card key={doItem.id} data-testid={`pdo-${doItem.id}`} className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-mono text-xs text-slate-500">{doItem.do_number}</p>
                <p className="font-medium mt-1">{pharmacies.find(p => p.id === doItem.pharmacy_id)?.name || '-'}</p>
                <p className="text-xs text-slate-500">Issued: {new Date(doItem.issued_at).toLocaleDateString()}</p>
                <div className="mt-2 space-y-1">
                  {doItem.line_items.map((li, idx) => (
                    <div key={idx} className="text-xs text-slate-700">
                      {products.find(p => p.id === li.product_id)?.name} — Batch {li.batch_number} — Exp {String(li.expiry_month).padStart(2,'0')}/{li.expiry_year} — Qty {li.quantity}
                    </div>
                  ))}
                </div>
              </div>
              <Badge className={doItem.status === 'verified' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}>
                {doItem.status}
              </Badge>
            </div>
          </Card>
        ))}
        {dos.length === 0 && <p className="text-center py-8 text-slate-500">No partner DOs yet</p>}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Create Partner Direct-Delivery DO</h3>
            <div className="space-y-4">
              {isAdmin && (
                <div>
                  <Label>Partner (on behalf of)</Label>
                  <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select partner" /></SelectTrigger>
                    <SelectContent>{partners.map(p => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Pharmacy</Label>
                <Select value={form.pharmacy_id} onValueChange={(v) => setForm({ ...form, pharmacy_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select pharmacy" /></SelectTrigger>
                  <SelectContent>{pharmacies.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Line Items</Label>
                {form.line_items.map((li, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 mt-2">
                    <div className="col-span-3">
                      <Select value={li.product_id} onValueChange={(v) => updateLine(idx, 'product_id', v)}>
                        <SelectTrigger><SelectValue placeholder="Product" /></SelectTrigger>
                        <SelectContent>{partnerProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Input className="col-span-3" placeholder="Batch #" value={li.batch_number} onChange={(e) => updateLine(idx, 'batch_number', e.target.value)} />
                    <Input className="col-span-2" type="number" placeholder="MM" min="1" max="12" value={li.expiry_month} onChange={(e) => updateLine(idx, 'expiry_month', e.target.value)} />
                    <Input className="col-span-2" type="number" placeholder="YYYY" value={li.expiry_year} onChange={(e) => updateLine(idx, 'expiry_year', e.target.value)} />
                    <Input className="col-span-2" type="number" placeholder="Qty" min="1" value={li.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} />
                    <div className="col-span-12 sm:col-auto flex justify-end">
                      {form.line_items.length > 1 && <Button size="sm" variant="outline" onClick={() => removeLineItem(idx)}><Trash size={14} /></Button>}
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addLineItem} className="mt-2"><Plus size={14} className="mr-1" /> Add</Button>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button data-testid="submit-pdo-btn" onClick={handleSubmit} style={{ backgroundColor: '#1E3A8A' }}>Create DO</Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
